import { Card, Typography, Box, alpha, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton, Tooltip } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import NoteIcon from '@mui/icons-material/Note';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { agreementApi } from "api";
import { AgreementEntity, UpdateAgreementDto } from "api/generated";
import { format } from "date-fns";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    maxHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: `0 12px 48px ${alpha(theme.palette.common.black, 0.12)}`,
        transform: 'translateY(-2px)',
    },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
    },
}));

const NotesContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: alpha(theme.palette.primary.main, 0.1),
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: alpha(theme.palette.primary.main, 0.3),
        borderRadius: '3px',
        '&:hover': {
            background: alpha(theme.palette.primary.main, 0.5),
        },
    },
}));


const TimeframeBox = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    fontSize: '0.75rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
}));

const BlurredContainer = styled(Box)(({ theme }) => ({
    filter: 'blur(8px)',
    pointerEvents: 'none',
    userSelect: 'none',
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(4px)',
        zIndex: 1,
    },
}));

const BlurOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
    background: alpha(theme.palette.background.paper, 0.95),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.warning.main}`,
    textAlign: 'center',
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
}));

const EditableNoteBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    background: alpha(theme.palette.background.default, 0.6),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    backdropFilter: 'blur(8px)',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    '&:hover': {
        background: alpha(theme.palette.background.default, 0.8),
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
        '& .edit-button': {
            opacity: 1,
        },
    },
}));

const EditButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
}));


interface Props {
    t: (key: string) => string;
    employeeId: number;
    seasonId: number | null;
    contractId: number | null;
    encryptionKey?: string;
}

const EmployeeAgreementNotes: FC<Props> = ({ t, employeeId, seasonId, contractId, encryptionKey }) => {
    const [agreements, setAgreements] = useState<AgreementEntity[]>([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingAgreement, setEditingAgreement] = useState<AgreementEntity | null>(null);
    const [editingNotes, setEditingNotes] = useState('');
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchAgreements = async () => {
            if (employeeId) {
                const response = await agreementApi.findAll(employeeId, seasonId ?? undefined, contractId ?? undefined);
                setAgreements(response.data);
            }
        };
        fetchAgreements();
    }, [employeeId, seasonId, contractId]);

    const handleEditNote = (agreement: AgreementEntity) => {
        setEditingAgreement(agreement);
        setEditingNotes(agreement.notes || '');
        setIsCreatingNew(false);
        setEditModalOpen(true);
    };

    const handleCreateNote = (agreement?: AgreementEntity) => {
        const target = agreement ?? agreements.find(a => !a.notes || a.notes.trim() === '') ?? agreements[0];
        if (target) {
            setEditingAgreement(target);
            setEditingNotes(target.notes || '');
            setIsCreatingNew(!target.notes || target.notes.trim() === '');
            setEditModalOpen(true);
        }
    };

    const handleSaveNote = async () => {
        if (!editingAgreement) return;

        setIsSaving(true);
        try {
            const updateData: UpdateAgreementDto = {
                notes: editingNotes.trim()
            };

            await agreementApi.update(editingAgreement.id.toString(), updateData);

            // Update the local state
            setAgreements(prev => prev.map(agreement => 
                agreement.id === editingAgreement.id 
                    ? { ...agreement, notes: editingNotes.trim() }
                    : agreement
            ));

            setEditModalOpen(false);
            setEditingAgreement(null);
            setEditingNotes('');
        } catch (error) {
            console.error('Failed to save note:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setEditingAgreement(null);
        setEditingNotes('');
        setIsCreatingNew(false);
    };

    const isEncryptionDisabled = !encryptionKey || encryptionKey === '';

    if (!agreements.length) {
        return (
            <StyledCard>
                <HeaderBox>
                    <NoteIcon />
                    <Typography variant="h6" fontWeight={600}>
                        {t('report.agreementNotes')}
                    </Typography>
                </HeaderBox>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('employees.calendar.noNotes')}
                </Typography>
            </StyledCard>
        );
    }

    const notesContent = (
        <Box position="relative" height="100%">
            <NotesContainer>
                {agreements.map((agreement, index) => (
                    <EditableNoteBox key={agreement.id}>
                        {!isEncryptionDisabled && (
                            <Tooltip title={agreement.notes && agreement.notes.trim() !== '' ? t('common.edit') : t('common.add')}>
                                <EditButton 
                                    className="edit-button"
                                    size="small"
                                    onClick={() => agreement.notes && agreement.notes.trim() !== '' ? handleEditNote(agreement) : handleCreateNote(agreement)}
                                >
                                    {agreement.notes && agreement.notes.trim() !== '' ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                </EditButton>
                            </Tooltip>
                        )}
                        {agreements.length > 1 && (
                            <TimeframeBox>
                                {format(new Date(agreement.startedOn), 'dd.MM.yyyy')} -
                                {agreement.endedOn
                                    ? format(new Date(agreement.endedOn), 'dd.MM.yyyy')
                                    : t('report.present')}
                            </TimeframeBox>
                        )}
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                lineHeight: 1.7,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                paddingRight: 4
                            }}
                        >
                            {agreement.notes || t('employees.calendar.noNotes')}
                        </Typography>
                        {index < agreements.length - 1 && (
                            <Divider sx={{ my: 2, opacity: 0.1 }} />
                        )}
                    </EditableNoteBox>
                ))}
            </NotesContainer>
        </Box>
    );

    return (
        <StyledCard>
            <HeaderBox>
                <NoteIcon />
                <Typography variant="h6" fontWeight={600}>
                    {t('report.agreementNotes')}
                </Typography>
            </HeaderBox>
            {isEncryptionDisabled ? (
                <Box position="relative">
                    <BlurredContainer>
                        {notesContent}
                    </BlurredContainer>
                    <BlurOverlay>
                        <LockIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h6" color="warning.main" fontWeight={600} gutterBottom>
                            {t('encryption.notes.locked')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('encryption.notes.enterKey')}
                        </Typography>
                    </BlurOverlay>
                </Box>
            ) : (
                notesContent
            )}

            {/* Edit/Create Note Modal */}
            <Dialog 
                open={editModalOpen} 
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '80vh',
                        boxShadow: `0 24px 64px ${alpha('#000', 0.15)}`,
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                    <NoteIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                        {isCreatingNew ? t('employees.notes.create') : t('employees.notes.edit')}
                    </Typography>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 2 }}>
                    {editingAgreement && agreements.length > 1 && (
                        <TimeframeBox sx={{ mb: 2 }}>
                            {format(new Date(editingAgreement.startedOn), 'dd.MM.yyyy')} -
                            {editingAgreement.endedOn
                                ? format(new Date(editingAgreement.endedOn), 'dd.MM.yyyy')
                                : t('report.present')}
                        </TimeframeBox>
                    )}
                    
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        value={editingNotes}
                        onChange={(e) => setEditingNotes(e.target.value)}
                        placeholder={t('employees.notes.placeholder')}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />
                </DialogContent>
                
                <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
                    <Button 
                        onClick={handleCloseModal}
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        disabled={isSaving}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button 
                        onClick={handleSaveNote}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={isSaving}
                        sx={{
                            minWidth: 120,
                        }}
                    >
                        {isSaving ? t('common.saving') : t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledCard>
    );
};

export default EmployeeAgreementNotes; 